/* eslint-disable unicorn/no-null -- the engine API returns/accepts `null` (e.g. getNudge → UINudge | null). */
import {
  consoleLogger,
  createNudgeEngine,
  noopLogger,
  type NudgeEngine,
  type NudgeMode,
  type UINudge,
  type UserProfileVector,
} from '@convegenius/nudging-engine';
import i18n from 'i18next';

import Constants from '@/Constants';
import logger from '@/logger';
import useStore from '@/store';
import { getSessionItem, isDesktop, isLinux } from '@/utils/helper';

import { rewardStrategyForMode, type NudgingEvent } from './rewardStrategies';

const getHeaders = (): Record<string, string> => {
  const sessionId = getSessionItem<string>('sessionId');
  const studentId = getSessionItem<string>('studentId');
  const deviceId = localStorage.getItem('device-id');
  const accessLocation = localStorage.getItem('accessLocation');
  let packageNamePrefix: 'linux' | 'desktop' | 'pwa' = 'pwa';
  if (isDesktop()) packageNamePrefix = isLinux() ? 'linux' : 'desktop';
  return {
    ...(sessionId ? { 'session-id': sessionId } : {}),
    ...(studentId ? { 'student-id': studentId } : {}),
    ...(deviceId ? { 'device-id': deviceId } : {}),
    ...(accessLocation ? { 'access-location': accessLocation } : {}),
    'package-name': `${packageNamePrefix}-ai.convegenius.pal.${Constants.ENVIRONMENT}`,
    'version-name': `${Constants.APP_VERSION}/${Constants.GIT_INFO}`,
    'version-code': `${Constants.APP_VERSION}`,
    'app-language': i18n.language || 'en',
  };
};

const engine: NudgeEngine = createNudgeEngine({
  baseUrl: Constants.AUTH_BASE_URL,
  getStudentId: () => getSessionItem<string>('studentId'),
  getHeaders,
  language: i18n.language || 'en',
  logger: import.meta.env.DEV ? consoleLogger : noopLogger,
});

let userVector: UserProfileVector = {};
let started = false;

export async function initNudging(): Promise<void> {
  const { nudgesEnabled } = useStore.getState();
  if (!nudgesEnabled || started) return;
  const studentId = getSessionItem<string>('studentId');
  const hasToken = !!localStorage.getItem('accessToken');
  if (!studentId || !hasToken) return;

  started = true;
  try {
    await engine.resetSession();
    await engine.sync.downloadGlobalMatrices();
    userVector = (await engine.fetchUserVectorData()) ?? {};
    engine.sync.start();
  } catch {
    // eslint-disable-next-line require-atomic-updates
    started = false;
  }
}

export function teardownNudging(): void {
  try {
    engine.sync.stop();
  } finally {
    userVector = {};
    started = false;
  }
}

export async function requestNudge(
  mode: NudgeMode,
  triggerPoint?: string | null,
): Promise<UINudge | null> {
  const { nudgesEnabled } = useStore.getState();
  if (!nudgesEnabled) return null;
  try {
    return await engine.getNudge({ user: userVector, targetContext: mode, triggerPoint });
  } catch {
    return null;
  }
}

export async function rewardNudge(
  value: number | ((mode: string) => number | null),
  eventName?: string,
): Promise<void> {
  const { nudgesEnabled } = useStore.getState();
  if (!nudgesEnabled) return;
  try {
    if (typeof value === 'number') {
      logger.info('[nudge] reward (direct)', { value, eventName });
    }
    await engine.reward(value, eventName ? { eventName } : undefined);
  } catch (error) {
    logger.warn('[nudge] reward failed', error);
  }
}

export async function reportNudgeEvent(event: NudgingEvent): Promise<void> {
  const { nudgesEnabled } = useStore.getState();
  if (!nudgesEnabled) return;
  logger.info('[nudge] event reported', { name: event.name, params: event.params });
  await rewardNudge((mode) => {
    const reward = rewardStrategyForMode(mode)(event);
    logger.info('[nudge] strategy → reward', {
      mode,
      event: event.name,
      params: event.params,
      reward,
    });
    return reward;
  }, event.name);
}

export const nudgeSignals = {
  onAnswerSubmitted: (correct: boolean, attemptMs: number) => {
    const { nudgesEnabled } = useStore.getState();
    if (nudgesEnabled) engine.session.onAnswerSubmitted(correct, attemptMs);
  },
  onHintsAvailable: (count: number) => {
    const { nudgesEnabled } = useStore.getState();
    if (nudgesEnabled) engine.session.onHintsAvailable(count);
  },
  onHintClicked: () => {
    const { nudgesEnabled } = useStore.getState();
    if (nudgesEnabled) engine.session.onHintClicked();
  },
  setItemDifficulty: (difficulty: number) => {
    const { nudgesEnabled } = useStore.getState();
    if (nudgesEnabled) engine.session.setCurrentItemDifficulty(difficulty);
  },
  onVideoSkip: () => {
    const { nudgesEnabled } = useStore.getState();
    if (nudgesEnabled) engine.session.onVideoSkipClicked();
  },
  resetQuestionSet: () => {
    const { nudgesEnabled } = useStore.getState();
    if (nudgesEnabled) engine.session.resetQuestionSet();
  },
};

export { NudgingConstants } from '@convegenius/nudging-engine';
export type { NudgeMode } from '@convegenius/nudging-engine';
export type { NudgingEvent } from './rewardStrategies';
export { engine as nudgeEngine };
